import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useFeature, useToggle } from 'Shared/utils/hooks';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';

// Components
import { WpPageUtilityNavBar } from 'Wpds/components/page-layout';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpMenu, WpMenuItem } from 'Wpds/components/menu';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpDrawerV2, WpDrawerV2Body } from 'Wpds/components/drawer';
import GiveFeedbackOrKudosModal, {
  FlowType,
} from 'Shared/components/GiveFeedbackOrKudosModal/GiveFeedbackOrKudosModal';
import CreateAiOrDtModal, { ItemType } from 'Shared/components/CreateDtOrAiModal/CreateDtOrAiModal';
import DayCalendarMeetings from 'Shared/components/DayCalendarMeetings/DayCalendarMeetings';

// Styles
import './HomePageUtilityNavBar.scss';
import { Feature } from 'Shared/types/Organization.type';

const HomePageUtilityNavBar = () => {
  const history = useHistory();
  const { isActive: isKudosActive } = useFeature(Feature.kudos);

  const { isXtraLarge } = useResponsiveScreenWidth({
    isAtLeast: true,
    breakpoints: { xl: 1360 },
  });

  const [isGiveFeedbackOrKudosModalOpen, toggleIsGiveFeedbackOrKudosModalOpen] = useToggle(false);
  const [isDrawOpen, toggleIsOpen] = useToggle(false);

  const [recognitionFlowType, setRecognitionFlowType] = useState<FlowType>(null);
  const [createDtOrAiType, setCreateDtOrAiType] = useState<ItemType>(null);

  const isCreateDtOrAiModalOpen = createDtOrAiType !== null;

  const handleDiscussionTopicClick = useCallback(() => {
    setCreateDtOrAiType(ItemType.discussionTopic);
  }, []);

  const handleActionItemClick = useCallback(() => {
    setCreateDtOrAiType(ItemType.actionItem);
  }, []);

  const handleCreateNewGoalClick = useCallback(() => {
    history.push(PageUrls.GOALS_NEW_PAGE);
  }, []);

  const handleKudosClick = () => {
    setRecognitionFlowType('kudos');
    toggleIsGiveFeedbackOrKudosModalOpen();
  };

  const handleGiveFeedbackFlow = () => {
    setRecognitionFlowType('feedback');
    toggleIsGiveFeedbackOrKudosModalOpen();
  };

  const baseClassName = 'HomePageUtilityNavBar';
  return (
    <>
      <WpPageUtilityNavBar className={baseClassName} width="full">
        <WpMenu direction="horizontal">
          <WpMenuItem exact to={PageUrls.HOME_PAGE}>
            Today&apos;s Schedule
          </WpMenuItem>
          <WpMenuItem to={PageUrls.CURRENT_USER_PROFILE_PAGE_HOME}>Your Profile</WpMenuItem>
          <WpMenuItem to={PageUrls.TEAM_PAGE}>Team Action Items</WpMenuItem>
          <WpMenuItem to={PageUrls.SURFACEAI_PAGE}>Surface AI</WpMenuItem>
        </WpMenu>
        <WpButtonGroup>
          {isXtraLarge ? (
            <>
              {isKudosActive && (
                <WpButton variant="tertiary" size="sm" onClick={handleKudosClick}>
                  <WpIcon name="gem" />
                  Give Kudos
                </WpButton>
              )}
              <WpButton variant="tertiary" size="sm" onClick={handleGiveFeedbackFlow}>
                <WpIcon name="hand-holding-heart" />
                Give Feedback
              </WpButton>
            </>
          ) : (
            <WpButton variant="tertiary" size="sm" onClick={toggleIsOpen}>
              <WpIcon name="calendar-day" />
            </WpButton>
          )}
          <WpDropdown placement="bottom-end" withBoxShadow>
            <WpDropdownToggle>
              <WpButton size="sm">
                <WpIcon name="plus-circle" />
                Add Item
                <WpIcon name="angle-down" />
              </WpButton>
            </WpDropdownToggle>
            <WpDropdownMenu>
              <WpDropdownMenuItem onClick={handleDiscussionTopicClick}>
                <WpIcon name="comment-alt-check" />
                Discussion Topic
              </WpDropdownMenuItem>
              <WpDropdownMenuItem onClick={handleActionItemClick}>
                <WpIcon name="check-square" />
                Action Item
              </WpDropdownMenuItem>
              <WpDropdownMenuItem onClick={handleCreateNewGoalClick}>
                <WpIcon name="bullseye-arrow" />
                Goal
              </WpDropdownMenuItem>
              {!isXtraLarge && (
                <>
                  <WpDropdownMenuDivider />
                  {isKudosActive && (
                    <WpDropdownMenuItem onClick={handleKudosClick}>
                      <WpIcon name="gem" />
                      Give Kudos
                    </WpDropdownMenuItem>
                  )}
                  <WpDropdownMenuItem onClick={handleGiveFeedbackFlow}>
                    <WpIcon name="hand-holding-heart" />
                    Give Feedback
                  </WpDropdownMenuItem>
                </>
              )}
            </WpDropdownMenu>
          </WpDropdown>
        </WpButtonGroup>
      </WpPageUtilityNavBar>
      <GiveFeedbackOrKudosModal
        flow={recognitionFlowType}
        isOpen={isGiveFeedbackOrKudosModalOpen}
        toggleIsOpen={toggleIsGiveFeedbackOrKudosModalOpen}
      />
      {isCreateDtOrAiModalOpen && (
        <CreateAiOrDtModal
          isOpen={createDtOrAiType !== null}
          toggleIsOpen={() => setCreateDtOrAiType(null)}
          itemType={createDtOrAiType}
        />
      )}
      {isDrawOpen && (
        <WpDrawerV2 isOpen={isDrawOpen} toggleIsOpen={toggleIsOpen} title="">
          <WpDrawerV2Body>
            <DayCalendarMeetings />
          </WpDrawerV2Body>
        </WpDrawerV2>
      )}
    </>
  );
};

export default HomePageUtilityNavBar;
